import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import PaymentModal from '../components/PaymentModal';

// Share Button Component
const ShareButtons = ({ title, url, description }) => {
  const [copied, setCopied] = useState(false);
  
  const shareUrl = encodeURIComponent(url);
  const shareTitle = encodeURIComponent(title);
  const shareText = encodeURIComponent(description || title);
  
  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [url]);

  const handleNativeShare = useCallback(async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: title,
          text: description,
          url: url,
        });
      } catch (err) {
        // User cancelled or share failed - this is expected behavior
        console.debug('Share cancelled or failed:', err);
      }
    }
  }, [title, description, url]);

  return (
    <div className="share-buttons-container" data-testid="share-buttons">
      <span className="share-label">Share this story:</span>
      <div className="share-buttons">
        {/* Twitter/X */}
        <a
          href={`https://twitter.com/intent/tweet?text=${shareText}&url=${shareUrl}`}
          target="_blank"
          rel="noopener noreferrer"
          className="share-button share-twitter"
          data-testid="share-twitter"
          aria-label="Share on Twitter"
        >
          <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
          </svg>
        </a>
        
        {/* Facebook */}
        <a
          href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`}
          target="_blank"
          rel="noopener noreferrer"
          className="share-button share-facebook"
          data-testid="share-facebook"
          aria-label="Share on Facebook"
        >
          <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
          </svg>
        </a>
        
        {/* LinkedIn */}
        <a
          href={`https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`}
          target="_blank"
          rel="noopener noreferrer"
          className="share-button share-linkedin"
          data-testid="share-linkedin"
          aria-label="Share on LinkedIn"
        >
          <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
          </svg>
        </a>
        
        {/* Copy Link */}
        <button
          onClick={handleCopyLink}
          className={`share-button share-copy ${copied ? 'copied' : ''}`}
          data-testid="share-copy"
          aria-label="Copy link to clipboard"
        >
          {copied ? (
            <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
              <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
            </svg>
          )}
        </button>
        
        {/* Native Share (mobile) */}
        {typeof navigator !== 'undefined' && navigator.share && (
          <button
            onClick={handleNativeShare}
            className="share-button share-native"
            data-testid="share-native"
            aria-label="Share"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
              <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z"/>
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

// Full story content data - optimized for SEO/AEO
const storiesContent = {
  'dooz-wilsons-creek': {
    // Meta info
    category: 'PEOPLE',
    publishDate: 'DEC 13, 2024',
    publishDateISO: '2024-12-13',
    modifiedDate: '2024-12-13',
    author: 'Free Flow Films',
    readTime: '5 min read',
    
    // SEO Meta
    metaTitle: 'Dooz in Washed Away Documentary | Melbourne Hip-Hop Artist | Australian Flood Documentary',
    metaDescription: 'Discover how Melbourne hip-hop artist Dooz brings raw truth to the award-winning Australian documentary Washed Away, capturing the human spirit during Australia\'s worst flood disaster in the Northern Rivers.',
    keywords: 'Dooz, Washed Away documentary, Australian documentary, Melbourne hip-hop artist, Northern Rivers floods, Wilsons Creek community, 2022 floods, Australian floods documentary, Lismore floods, community resilience, mental health awareness',
    
    // Page content
    title: 'UNSUNG HEROES: DOOZ & THE WILSONS CREEK COMMUNITY',
    heroImage: 'https://customer-assets.emergentagent.com/job_bcb7959a-5c11-4a02-a3be-bbbaf5b483ed/artifacts/5msjtw0d_Dooz2.jpg',
    heroImageAlt: 'Dooz - Melbourne hip-hop artist featured in Washed Away Australian documentary',
    
    // Main content
    mainHeading: 'FROM HIP-HOP TO HEROISM: HOW DOOZ BRINGS RAW TRUTH TO AUSTRALIAN DOCUMENTARY "WASHED AWAY"',
    
    introParagraphs: [
      'In the powerful new Australian documentary "Washed Away," Melbourne hip-hop artist Dooz emerges as one of the most compelling voices capturing the human spirit during Australia\'s worst flood disaster.',
      'His story showcases how art and advocacy can merge in times of crisis, making him a standout figure in this ground-breaking 2024 documentary.'
    ],
    
    // Unique section
    uniqueSectionTitle: 'WHAT MAKES THIS STORY UNIQUE',
    uniquePoints: [
      'Real-time footage of community response during Australia\'s worst flood',
      'Features prominent Australian musicians, including Silverchair and Pete Murray',
      'Official selection for Byron Bay International Film Festival 2024',
      'Highlights grassroots solidarity in the face of natural disaster',
      'Showcases authentic Australian spirit through music and community action'
    ],
    
    // Body paragraphs
    bodyParagraphs: [
      'Beyond his musical talents, Dooz emerges as a voice for mental health awareness and community resilience. His participation in "Washed Away" adds an authentic perspective to this landmark Australian documentary, helping tell the story of how ordinary people became heroes during extraordinary times.',
      'For viewers seeking powerful Australian documentaries that capture the national spirit and human resilience, "Washed Away" offers an unforgettable journey through one of our country\'s most challenging natural disasters. Dooz\'s story exemplifies the heart and hope at its core.'
    ],
    
    // CTA
    ctaText: 'Streaming Now "Washed Away" at washedaway.au and for more information about this ground-breaking Australian documentary.',
    
    // Schema.org Person data for Dooz
    personSchema: {
      name: 'Dooz',
      alternateName: 'Melbourne hip-hop artist Dooz',
      description: 'Melbourne hip-hop artist and mental health advocate featured in the Australian documentary Washed Away',
      jobTitle: 'Hip-hop Artist, Community Advocate',
      knowsAbout: ['Hip-hop music', 'Mental health awareness', 'Community resilience', 'Northern Rivers flood recovery'],
    },
    
    // FAQ Schema for AEO (Answer Engine Optimization)
    faqSchema: [
      {
        question: 'Who is Dooz in the Washed Away documentary?',
        answer: 'Dooz is a Melbourne hip-hop artist who emerged as one of the most compelling voices in the Australian documentary "Washed Away." He captures the human spirit during Australia\'s worst flood disaster and serves as a voice for mental health awareness and community resilience.'
      },
      {
        question: 'What is the Washed Away documentary about?',
        answer: 'Washed Away is an award-winning Australian documentary capturing the devastating 2022 Northern Rivers floods. It showcases real-time footage of community response, features prominent Australian musicians including Silverchair and Pete Murray, and highlights how ordinary people became heroes during Australia\'s worst natural disaster.'
      },
      {
        question: 'Where can I watch Washed Away documentary?',
        answer: 'You can stream the Washed Away documentary at washedaway.au. The documentary offers 48-hour streaming access, with 50% of profits going to rebuild homes through the Hands & Hearts Project.'
      },
      {
        question: 'What makes Dooz\'s story in Washed Away unique?',
        answer: 'Dooz\'s story showcases how art and advocacy can merge in times of crisis. As a hip-hop artist, he brings an authentic perspective to the documentary, demonstrating how music and community action can help with mental health awareness and community resilience during natural disasters.'
      }
    ]
  },
  
  'mick-fanning-gold-coast': {
    // Meta info
    category: 'FILM · PEOPLE',
    publishDate: 'DEC 13, 2024',
    publishDateISO: '2024-12-13',
    modifiedDate: '2024-12-13',
    author: 'Free Flow Films',
    readTime: '8 min read',
    
    // SEO Meta
    metaTitle: 'Mick Fanning, Caz Goulding, Renee Honey & Gold Coast Community | Washed Away Documentary',
    metaDescription: 'How Gold Coast surfers united a community during Australia\'s greatest flood rescue. Mick Fanning, Caz Goulding, Renee Honey and the Gold Coast community led the response when the Northern Rivers flooded in 2022.',
    keywords: 'Mick Fanning, Caz Goulding, Renee Honey, Gold Coast surfers, Northern Rivers floods, 2022 floods, Washed Away documentary, Joel Parkinson, Starlink, Elon Musk, Chris Hemsworth, flood rescue, Australian documentary, Lismore floods, community heroes, Jarrah Tutton, Hands and Hearts',
    
    // Page content
    title: 'UNSUNG HEREOS: MICK FANNING, CAZ GOULDING, RENEE HONEY & THE GOLD COAST COMMUNITY',
    heroImage: 'https://customer-assets.emergentagent.com/job_bcb7959a-5c11-4a02-a3be-bbbaf5b483ed/artifacts/letxbtxa_Mickjpg.jpg',
    heroImageAlt: 'Mick Fanning on jet ski during Northern Rivers flood rescue - Washed Away Documentary',
    
    // Main content
    mainHeading: 'WHEN THE WATERS ROSE: HOW GOLD COAST SURFERS UNITED A COMMUNITY | THE UNTOLD STORY BEHIND AUSTRALIA\'S GREATEST FLOOD RESCUE',
    
    introParagraphs: [
      'MICK FANNING, a three-time world surfing champion, led a bold flood rescue in his hometown. Using jet skis, he and his team created essential lifelines for residents. Amid a communication blackout, his tweet to Elon Musk resulted in a rapid Starlink deployment, providing high-speed internet within 48 hours. This improved communication and coordination, setting a new standard for flood responses. Their efforts are captured in "Washed Away," showcasing community resilience in overcoming disaster.',
      'When the flood waters hit the Northern Rivers on February 28, 2022, it wasn\'t government agencies that led the response. It was a group of surfers, led by three-time world champion Mick Fanning, who became the unlikely architects of Australia\'s most remarkable community-powered rescue mission.'
    ],
    
    // Multiple content sections
    sections: [
      {
        title: 'THE CALL THAT CHANGED EVERYTHING',
        paragraphs: [
          '"Mate, it\'s so heavy!" Joel Parkinson\'s urgent call to Mick Fanning and crew sparked a six-day rescue operation that redefined community response. Within hours, the Tweed Heads boat ramp transformed into a makeshift emergency hub, with surfers using their intimate knowledge of water dynamics to navigate the treacherous flood waters.'
        ]
      },
      {
        title: 'A COMMUNITY OF HEROES EMERGES',
        paragraphs: [
          'While Mick Fanning\'s name draws headlines, "Washed Away" reveals the true heart of this story – the extraordinary ordinary people who stepped up: Patchy and Rick risked dangerous waters to deliver insulin through flood waters, driven by Patchy\'s understanding as a father of a diabetic son.',
          'Luke completed a critical hospital blood delivery in 51 minutes when he was waiting for official channels for 48 hours.',
          'Renee and Benji Honey, Caz Goulding, Hammo, and Jarrah Tutton, who worked tirelessly alongside countless unnamed heroes Renee, Benji Honey + Local who turned a corner store warehouse into an emergency response centre'
        ]
      },
      {
        title: 'INNOVATION BORN FROM NECESSITY',
        paragraphs: [
          'When communication systems failed, community innovation soared. A tweet from Fanning to Elon Musk, amplified by Chris Hemsworth\'s connections, secured vital Starlink units within 48 hours – a solution typically reserved for international disaster zones.'
        ]
      },
      {
        title: 'THE BATTLE CONTINUES: 2.5 YEARS ON',
        paragraphs: [
          '"The waters receded, but the struggle didn\'t," reflects Fanning, touring areas where: 40% of homes remain uninhabitable.',
          'The local IGA – a community lifeline – still operates from temporary facilities',
          'Families navigate complex grant applications with limited success Mental health emerges as a silent secondary crisis. Now the IGA has been demolished.'
        ]
      },
      {
        title: 'HANDS AND HEARTS: THE COMMUNITY FIGHTS BACK',
        paragraphs: [
          'Through initiatives like Hands and Hearts, the community continues to lead the recovery:'
        ],
        bulletPoints: [
          '48 homes rebuilt for vulnerable residents',
          'Goal set to restore 100 family homes',
          'Ongoing support networks established',
          'Local knowledge driving recovery efforts'
        ],
        sectionLink: {
          url: 'https://www.mrnc.com.au/pages/hands-hearts-project',
          text: 'Learn more about Hands & Hearts'
        }
      },
      {
        title: 'AN EPIC AUSSIE MUSIC SCORE',
        paragraphs: [
          'The story of community resilience is amplified by an extraordinary musical collaboration featuring Australian icons:'
        ],
        bulletPoints: [
          'Jon Stevens (Noiseworks)',
          'Ziggy Alberts',
          'Pete Murray',
          'Silverchair',
          'Tex Perkins',
          'Goanna\'s "Solid Rock" serves as an anthem of strength.',
          'Felipe Baldomir and Ben Jenz provide powerful original scores.'
        ]
      },
      {
        title: 'MORE THAN A DOCUMENTARY',
        paragraphs: [
          '"Washed Away" isn\'t just documenting history – it\'s actively creating change. Every viewing directly supports ongoing flood recovery efforts through MRNC\'s Hands and Hearts project. As climate-related disasters become more frequent, the lessons from these surf champions-turned-rescuers become increasingly valuable.'
        ]
      },
      {
        title: 'HOW YOU CAN HELP',
        paragraphs: [
          'Watch "Washed Away" – Every paid stream supports recovery efforts',
          'Attend the launch event at Balter Brewery',
          'Follow and share:',
          'Instagram: @washedawaymovie',
          'Facebook: /washedawaymovie'
        ]
      },
      {
        title: 'THE LEGACY LIVES ON',
        paragraphs: [
          '"That\'s what we do," says Fanning. "We support each other, we support our neighbours and let them know they are not forgotten." In an era where natural disasters are becoming more frequent, this story of community resilience offers hope and a blueprint for future crisis response.'
        ]
      }
    ],
    
    // CTA
    ctaText: 'Stream "Washed Away" now — witness the incredible true story of community, courage, and resilience.',
    
    // Schema.org data
    personSchema: {
      name: 'Mick Fanning',
      alternateName: 'Three-time World Surfing Champion',
      description: 'Mick Fanning led the Gold Coast community flood rescue effort during the 2022 Northern Rivers floods, as documented in Washed Away',
      jobTitle: 'Professional Surfer, Community Leader',
      knowsAbout: ['Surfing', 'Flood rescue', 'Community response', 'Northern Rivers floods'],
    },
    
    // FAQ Schema for AEO
    faqSchema: [
      {
        question: 'What did Mick Fanning do during the Northern Rivers floods?',
        answer: 'Mick Fanning, a three-time world surfing champion, led a bold flood rescue in his hometown using jet skis. He and his team created essential lifelines for residents, and his tweet to Elon Musk resulted in a rapid Starlink deployment within 48 hours, improving communication during the crisis.'
      },
      {
        question: 'Who were the heroes of the 2022 Northern Rivers flood rescue?',
        answer: 'The flood rescue was led by Gold Coast surfers including Mick Fanning, Joel Parkinson, Caz Goulding, Renee and Benji Honey, Hammo, and Jarrah Tutton. Ordinary heroes like Patchy, Rick, and Luke also risked their lives to deliver critical supplies like insulin and blood.'
      },
      {
        question: 'How did Starlink help during the Northern Rivers floods?',
        answer: 'When communication systems failed during the floods, Mick Fanning tweeted Elon Musk for help. Amplified by Chris Hemsworth\'s connections, this secured vital Starlink units within 48 hours – a solution typically reserved for international disaster zones – restoring communication and coordination.'
      },
      {
        question: 'What music is in Washed Away documentary?',
        answer: 'The Washed Away soundtrack features iconic Australian artists including Silverchair, Ziggy Alberts, Goanna, Tex Perkins, Pete Murray, and Noiseworks (Jon Stevens), plus original scores by Ben Jansz and Felipe Baldomir.'
      },
      {
        question: 'Where can I watch Washed Away documentary?',
        answer: 'You can stream Washed Away exclusively at washedaway.au. The documentary is available for streaming with 48-hour access for $12.69 AUD.'
      },
      {
        question: 'What is the Hands and Hearts initiative in Washed Away?',
        answer: 'Hands and Hearts is a community-led recovery initiative featured in Washed Away. The same volunteers who led the flood rescue are now rebuilding homes for displaced families — 48 homes completed so far, with a goal of 100. It represents the ongoing spirit of community that the documentary celebrates.'
      }
    ]
  },
  
  'michael-barnes-rotorwing': {
    // Meta info
    category: 'PEOPLE',
    publishDate: 'DEC 13, 2024',
    publishDateISO: '2024-12-13',
    modifiedDate: '2024-12-13',
    author: 'Free Flow Films',
    readTime: '6 min read',
    
    // SEO Meta
    metaTitle: 'Michael Barnes & Rotorwing Helicopters | Northern Rivers Flood Heroes | Washed Away Documentary',
    metaDescription: 'Discover how Michael Barnes turned his helicopter base into a rescue sanctuary during Australia\'s worst floods. Watch his inspiring story in the award-winning documentary Washed Away.',
    keywords: 'Michael Barnes, Rotorwing Helicopters, Washed Away documentary, Northern Rivers floods, helicopter rescue, 2022 floods, Australian floods documentary, Lismore floods, flood volunteers, community heroes',
    
    // Page content
    title: 'UNSUNG HEROES: MICHAEL BARNES & THE ROTORWING HELICOPTERS VOLUNTEERS',
    heroImage: 'https://customer-assets.emergentagent.com/job_bcb7959a-5c11-4a02-a3be-bbbaf5b483ed/artifacts/331ge3f9_Barnsey2.jpg',
    heroImageAlt: 'Michael Barnes and Rotorwing Helicopters during Northern Rivers flood rescue operations - Washed Away Documentary',
    
    // Main content - no main heading, paragraphs flow directly
    mainHeading: '',
    
    introParagraphs: [
      'Amid the catastrophic floods that swept through the Northern Rivers in 2022, one figure quietly emerged as a beacon of hope: Michael Barnes. His story, now tenderly captured in "Washed Away," reveals a path shaped by unwavering resolve, boundless empathy, and a belief in the strength of working together when everything else falls apart.',
      'In those harrowing hours, as floodwaters rose and chaos reigned, Michael\'s first instinct was to ensure his family\'s safety. But it didn\'t end there. Standing at the threshold of disaster, he turned his modest helicopter base into a sanctuary, inviting in anyone willing to lend a hand, even those uncertain of their own abilities. Under Michael\'s guidance, a disparate group of volunteers gathered, each committed to venturing into the unknown, determined to pull neighbours and strangers alike from danger.',
      'Yet "Washed Away" is not merely a chronicle of dramatic rescues. At its core, it\'s a quiet meditation on the human spirit. When seen through the film\'s intimate lens, the grit and kindness that defined these collective efforts shine even brighter. Ordinary people, each with their own fears and limitations, formed a tapestry of unity. In that fragile moment, they discovered a truth that would bind them together: that in crisis, we have the power to lift one another up.',
      'Long after the floodwaters receded, the story Michael and his team wrote upon the hearts of their community endures. "Washed Away" gently reminds us that heroism often comes not from grand gestures, but from everyday people stepping forward with open hands and open hearts. In these small, compassionate acts, entire communities find the will to rebuild and rediscover hope.',
      'As the credits roll, viewers are left with a deeper understanding of resilience and the quiet courage that springs from our shared humanity. Michael Barnes\' journey stands as a testament that ordinary lives can illuminate our darkest moments. In a world prone to idolizing fictional saviours, "Washed Away" shows us that true heroes are those who stand shoulder-to-shoulder, giving what they can, expecting nothing in return.',
      'This is your chance to experience a story that transcends the screen. By claiming your seat, you join this narrative of hope and cooperation. You ensure that Michael\'s legacy, and the steadfast spirit of the Northern Rivers, continues to inspire others to reach out, rebuild, and find common purpose in times of hardship.',
      'Your support truly matters, helping keep alive a message that we are always stronger together.'
    ],
    
    // No sections for this story - it's a flowing narrative
    sections: [],
    
    // CTA
    ctaText: 'Stream "Washed Away" now at washedaway.au — experience Michael Barnes\' inspiring story of community, courage, and hope.',
    
    // Schema.org Person data
    personSchema: {
      name: 'Michael Barnes',
      description: 'Helicopter pilot and community hero who turned his base into a rescue sanctuary during the 2022 Northern Rivers floods, featured in the documentary Washed Away',
      jobTitle: 'Helicopter Pilot, Community Hero',
      knowsAbout: ['Helicopter operations', 'Flood rescue', 'Community resilience', 'Northern Rivers flood recovery', 'Volunteer coordination'],
    },
    
    // FAQ Schema for AEO
    faqSchema: [
      {
        question: 'Who is Michael Barnes in Washed Away documentary?',
        answer: 'Michael Barnes is a helicopter pilot who emerged as a beacon of hope during the 2022 Northern Rivers floods. He turned his helicopter base into a rescue sanctuary, coordinating volunteers to save neighbours and strangers from the floodwaters. His story is featured in the award-winning documentary "Washed Away."'
      },
      {
        question: 'What did Rotorwing Helicopters do during the floods?',
        answer: 'During the catastrophic 2022 Northern Rivers floods, Rotorwing Helicopters, led by Michael Barnes, became a volunteer rescue hub. The helicopter base was transformed into a sanctuary where volunteers gathered to conduct aerial rescues and save lives from the rising floodwaters.'
      },
      {
        question: 'Where can I watch Michael Barnes story in Washed Away?',
        answer: 'You can stream the Washed Away documentary featuring Michael Barnes\' inspiring story at washedaway.au. The documentary offers 48-hour streaming access, with 50% of profits going to rebuild homes through the Hands & Hearts Project.'
      },
      {
        question: 'What makes Michael Barnes story unique in Washed Away?',
        answer: 'Michael Barnes\' story in Washed Away is a quiet meditation on the human spirit. It shows how ordinary people, each with their own fears and limitations, formed a tapestry of unity during Australia\'s worst flood disaster. His journey is a testament that heroism comes from everyday people stepping forward with open hands and open hearts.'
      }
    ]
  },
  
  'michelle-bryant': {
    // Meta info
    category: 'PEOPLE',
    publishDate: 'DEC 13, 2024',
    publishDateISO: '2024-12-13',
    modifiedDate: '2024-12-13',
    author: 'Free Flow Films',
    readTime: '4 min read',
    
    // SEO Meta
    metaTitle: 'Michelle Bryant | Animal Rescue Hero | Washed Away Documentary | Lismore Floods',
    metaDescription: 'Discover how Michelle Bryant\'s deep love for animals made her a beacon of compassion during the devastating 2022 Lismore floods. Watch her inspiring story in Washed Away.',
    keywords: 'Michelle Bryant, Washed Away documentary, Lismore floods, animal rescue, 2022 floods, Australian floods documentary, animal welfare, flood volunteers, Northern Rivers, community heroes',
    
    // Page content
    title: 'UNSUNG HERO: MICHELLE BRYANT',
    heroImage: 'https://customer-assets.emergentagent.com/job_bcb7959a-5c11-4a02-a3be-bbbaf5b483ed/artifacts/u9dwxzqg_Shelly.jpg',
    heroImageAlt: 'Michelle Bryant - Animal rescue hero featured in Washed Away Documentary',
    
    // Main content - no main heading, paragraphs flow directly
    mainHeading: '',
    
    introParagraphs: [
      'Amid the aftermath of the devastating 2022 Lismore floods, a figure driven by a deep love for animals emerged as a heart of pure compassion and dedication. Michelle Bryant\'s remarkable journey, vividly portrayed in the film "Washed Away," serves as a powerful reminder of the profound impact one person\'s bond with animals can have on a community in crisis.',
      'As the floodwaters surged, Michelle\'s concern extended beyond personal safety, encompassing the well-being of the animals affected by the disaster. "Washed Away" skilfully captures her transition from an empathetic individual to a determined leader. She channelled her passion into orchestrating essential relief efforts, gathering vital medical supplies, and providing aid to countless animals stranded by the catastrophe. Her unwavering commitment and tireless efforts played a pivotal role in both the animal kingdom and the community\'s recovery.',
      'Michelle\'s narrative speaks volumes about the significance of empathy and collective support, as well as the far-reaching impact of her compassion on the community\'s resilience. In a world often captivated by tales of grand heroism, Michelle\'s quiet dedication and love for animals emphasise that everyday individuals can shine as powerful symbols of hope, particularly when fuelled by a profound connection to the animal world.',
      'As you immerse yourself in the heartfelt narrative of "Washed Away," you\'re invited to witness Michelle Bryant\'s exceptional journey—one that embodies not only compassion and resilience, but also an unwavering devotion to the welfare of animals. This film stands as a tribute to those who, like Michelle, exemplify the transformative power of caring for the voiceless beings that share our planet.'
    ],
    
    // No sections for this story - it's a flowing narrative
    sections: [],
    
    // CTA
    ctaText: 'Stream "Washed Away" now at washedaway.au — experience Michelle Bryant\'s inspiring story of compassion and dedication.',
    
    // Schema.org Person data
    personSchema: {
      name: 'Michelle Bryant',
      description: 'Animal rescue hero who became a beacon of compassion during the 2022 Lismore floods, featured in the documentary Washed Away',
      jobTitle: 'Animal Welfare Advocate, Community Hero',
      knowsAbout: ['Animal rescue', 'Flood relief', 'Community resilience', 'Animal welfare', 'Northern Rivers flood recovery'],
    },
    
    // FAQ Schema for AEO
    faqSchema: [
      {
        question: 'Who is Michelle Bryant in Washed Away documentary?',
        answer: 'Michelle Bryant is an animal rescue hero featured in the award-winning documentary "Washed Away." During the devastating 2022 Lismore floods, she emerged as a beacon of compassion, dedicating herself to rescuing and caring for animals stranded by the rising waters.'
      },
      {
        question: 'What did Michelle Bryant do during the Lismore floods?',
        answer: 'During the 2022 Lismore floods, Michelle Bryant orchestrated essential relief efforts for animals, gathering vital medical supplies and providing aid to countless animals stranded by the catastrophe. Her unwavering commitment played a pivotal role in both the animal kingdom and the community\'s recovery.'
      },
      {
        question: 'Where can I watch Michelle Bryant\'s story?',
        answer: 'You can stream Washed Away featuring Michelle Bryant\'s inspiring story at washedaway.au. The documentary offers 48-hour streaming access, with 50% of profits going to rebuild homes through the Hands & Hearts Project.'
      },
      {
        question: 'Why is Michelle Bryant called an unsung hero?',
        answer: 'Michelle Bryant is called an unsung hero because her quiet dedication and love for animals emphasise that everyday individuals can shine as powerful symbols of hope. She transitioned from an empathetic individual to a determined leader, exemplifying the transformative power of caring for voiceless beings.'
      }
    ]
  },
  
  'terry-webster-stuart-mckay': {
    // Meta info
    category: 'FILM · PEOPLE',
    publishDate: 'DEC 11, 2024',
    publishDateISO: '2024-12-11',
    modifiedDate: '2024-12-11',
    author: 'Free Flow Films',
    readTime: '5 min read',
    
    // SEO Meta
    metaTitle: 'Terry Webster & Stuart McKay | Free Flow Films | Washed Away Documentary Filmmakers',
    metaDescription: 'Meet Terry Webster and Stuart McKay, the passionate filmmakers behind Free Flow Films and the award-winning Australian documentary Washed Away.',
    keywords: 'Terry Webster, Stuart McKay, Free Flow Films, Washed Away documentary, Australian filmmakers, documentary filmmaking, Northern Rivers, Australian cinema, independent film',
    
    // Page content
    title: 'MEET THE FILM MAKERS: TERRY WEBSTER & STUART MCKAY',
    heroImage: 'https://customer-assets.emergentagent.com/job_bcb7959a-5c11-4a02-a3be-bbbaf5b483ed/artifacts/3nuo5ks5_Terry_Stu2.jpg',
    heroImageAlt: 'Terry Webster and Stuart McKay - Filmmakers behind Washed Away Documentary',
    
    // Main content
    mainHeading: '',
    
    introParagraphs: [
      'Free Flow Films is a studio born from the passion and vision of Terry Webster and Stuart McKay. Free Flow Films mission is to craft films that delve into Australia\'s heart, exploring its REAL challenges and beauty through the lens of humanity, love and longevity.'
    ],
    
    // Multiple content sections
    sections: [
      {
        title: 'Backgrounds of Terry Webster and Stuart McKay.',
        paragraphs: [
          'Terry Webster has an illustrious career in films around the globe and is known for his strength in harsh environments. He has a keen eye for storytelling. His work as a DOP and editor, from the wilds of "Man Eats Wild" to the tales of ABC\'s "Fightback Farmers," showcases a dedication to films that inspire and provoke thought. Stuart McKay complements Terry\'s vision with his great music scores, sharing a boldness to traverse Australia\'s most challenging terrains to capture its stories. Like Terry, Stu\'s specialities are in tourism and films for humanity, and both are incredibly dedicated to being one of Australia\'s best film producers. They are a one-stop shop.'
        ]
      },
      {
        title: 'Genesis of Free Flow Films',
        paragraphs: [
          'The genesis of Free Flow Films was as dramatic as the stories they seek to tell. Witnessing the devastation and recovery of Australia\'s most costly disaster firsthand, Terry and Stuart were inspired by the community\'s stability. This experience lit the spark for Free Flow Films, a studio dedicated to telling stories of the great Australian spirit.'
        ]
      },
      {
        title: 'Community Engagement and Future Directions',
        paragraphs: [
          'By supporting Free Flow Films on Patreon, you become a part of a community dedicated to driving dialogue and action through compelling storytelling. Join us in our mission to make a difference. Support independent filmmaking and help us continue to craft films that inspire and provoke thought.'
        ]
      },
      {
        title: 'Challenges and Triumphs',
        paragraphs: [
          'Despite facing the industry\'s typical hurdles, including small budgets, legal matters, and the daunting task of bringing significant Australian stories to the screen, Free Flow Films has emerged. Their dedication to producing impactful cinema on a shoestring budget has showcased their creativity and commitment to their craft. But this comes as a challenge. It\'s only sustainable to tell these types of stories or, even more so, controversial documentaries, with support from an entertainment legal team, & realistic budgets. We aim to produce four compelling documentaries that start a conversation and, most importantly, spark action to assist with fixing the issue and enhance the quality of life. We are looking for a philanthropist with the same core values to partner with and work towards the mission.'
        ]
      },
      {
        title: 'Impact and Awareness.',
        paragraphs: [
          'Free Flow Films has become an Australian production company of attention, sparking conversations on critical issues like the Northern Rivers Floods through their documentary "Washed Away." Their work highlights the power of community and the urgent need for action, making a tangible impact on audiences and beyond.'
        ]
      }
    ],
    
    // CTA
    ctaText: 'Stream "Washed Away" now at washedaway.au — experience the powerful documentary from Free Flow Films.',
    
    // Schema.org Person data
    personSchema: {
      name: 'Terry Webster & Stuart McKay',
      description: 'Filmmakers and founders of Free Flow Films, creators of the award-winning Australian documentary Washed Away',
      jobTitle: 'Documentary Filmmakers',
      knowsAbout: ['Documentary filmmaking', 'Australian cinema', 'Storytelling', 'Community narratives', 'Northern Rivers'],
    },
    
    // FAQ Schema for AEO
    faqSchema: [
      {
        question: 'Who made the Washed Away documentary?',
        answer: 'Washed Away was created by directors Terry Webster and Stuart McKay of Free Flow Films, with Rebecca Matkovic-Helou as story producer, and SOCIALFUEL providing art direction and brand leadership. Free Flow Films is an Australian studio dedicated to crafting films that explore Australia\'s real challenges and beauty through the lens of humanity, love, and longevity.'
      },
      {
        question: 'What is Free Flow Films?',
        answer: 'Free Flow Films is an Australian documentary studio founded by Terry Webster and Stuart McKay. Their mission is to craft films that delve into Australia\'s heart, exploring real challenges and beauty through authentic storytelling.'
      },
      {
        question: 'Where can I watch films by Free Flow Films?',
        answer: 'You can stream Washed Away, the latest documentary from Free Flow Films, at washedaway.au. The film offers 48-hour streaming access, with 50% of profits going to rebuild homes through the Hands & Hearts Project.'
      },
      {
        question: 'What other films has Free Flow Films made?',
        answer: 'Free Flow Films is known for creating authentic Australian documentaries that give voice to communities and individuals whose stories deserve to be heard. Washed Away is their powerful documentary about the 2022 Northern Rivers floods.'
      }
    ]
  },
  
  'one-vision-productions': {
    // Meta info
    category: 'GRATITUDE',
    publishDate: 'DEC 11, 2024',
    publishDateISO: '2024-12-11',
    modifiedDate: '2024-12-11',
    author: 'Free Flow Films',
    readTime: '3 min read',
    
    // SEO Meta
    metaTitle: 'One Vision Productions | The People Who Helped Bring Washed Away To Life | Byron Bay Film Production',
    metaDescription: 'Discover how Frank Coorey and Mark Robertson from One Vision Productions became the backbone of Australia\'s most powerful flood documentary, Washed Away.',
    keywords: 'One Vision Productions, Frank Coorey, Mark Robertson, Byron Bay film production, Washed Away documentary, Australian documentary, mental health advocacy, Northern Rivers, Free Flow Films, Terry Webster, Stuart McKay',
    
    // Special hero banner style
    heroStyle: 'banner',
    
    // Page content
    title: 'THE BELIEVERS',
    heroImage: 'https://customer-assets.emergentagent.com/job_bcb7959a-5c11-4a02-a3be-bbbaf5b483ed/artifacts/gad07q6d_OVP.jpg',
    heroImageAlt: 'One Vision Productions - The heart and soul behind Washed Away Documentary',
    
    // Main content
    mainHeading: 'Great filmmakers recognise a great story. They recognised this one.',
    
    introParagraphs: [
      'After weeks of heavy filming in Lismore — deep in the grief, the mud, the overwhelming weight of Australia\'s worst natural disaster — Stuart and I made our way to Byron Bay. Exhausted. Carrying more than just equipment.',
      'That\'s where we met Frank Coorey and Mark Robertson from One Vision Productions.',
      'Frank is a highly respected producer with deep roots in the Australian film industry, and his reputation is well earned. But what struck us wasn\'t his credits — it was his instinct to simply help. He and Mark opened their doors without hesitation. Facilities, support, interviews, connections. They became a genuine bridge to the local community at a time when we needed exactly that.',
      'These weren\'t just industry contacts. These were people who showed up when it mattered most.'
    ],
    
    // Vimeo embed - placed after intro
    vimeoEmbed: {
      videoId: '822849828',
      title: 'One Vision Productions - Byron Bay Film Production'
    },
    
    // Sections after video
    sections: [
      {
        title: '',
        paragraphs: [
          'What One Vision Productions does beyond film is a story worth telling in its own right. Their work around children\'s mental health is extraordinary — quiet, consistent, and deeply impactful. In a world of noise, they chose meaningful action over empty gestures.',
          'Four years on, Frank and Mark are still in our corner. Still doing everything they possibly can to help get Washed Away seen by the audiences it deserves. That kind of loyalty doesn\'t come from a contract. It comes from character.',
          'To Frank, Mark, and the entire One Vision Productions family: thank you for believing in this story. Thank you for believing in us.'
        ]
      },
      {
        title: 'ABOUT ONE VISION PRODUCTIONS',
        paragraphs: [
          'One Vision Productions is a Byron Bay-based film production company dedicated to creating content that matters. Led by acclaimed producer Frank Coorey and Mark Robertson, they champion causes close to their hearts — particularly children\'s mental health and community wellbeing.',
          'Visit their website to learn more about their incredible work: onevisionproductions.com.au'
        ]
      }
    ],
    
    // External link
    externalLink: {
      url: 'https://onevisionproductions.com.au/',
      text: 'Visit One Vision Productions'
    },
    
    // CTA
    ctaText: 'Stream "Washed Away" now at washedaway.au — a film made possible by the extraordinary support of One Vision Productions.',
    
    // Schema.org data
    personSchema: {
      name: 'One Vision Productions',
      alternateName: '1 Vision Productions',
      description: 'Byron Bay film production company and key supporters of the Washed Away documentary, known for their work in children\'s mental health advocacy',
      jobTitle: 'Film Production Company',
      knowsAbout: ['Film production', 'Documentary filmmaking', 'Children\'s mental health', 'Community support', 'Byron Bay'],
    },
    
    // FAQ Schema for AEO
    faqSchema: [
      {
        question: 'Who is One Vision Productions?',
        answer: 'One Vision Productions is a Byron Bay-based film production company led by acclaimed producer Frank Coorey and Mark Robertson. They provided crucial support, facilities, and community connections that made the Washed Away documentary possible.'
      },
      {
        question: 'What role did One Vision Productions play in Washed Away?',
        answer: 'One Vision Productions became essential to Washed Away\'s creation. After weeks of filming in flood-devastated Lismore, Frank Coorey and Mark Robertson welcomed the filmmakers, providing facilities, support, interviews, and vital connections to the Byron Bay community. Four years on, they\'re still helping get the documentary seen by audiences.'
      },
      {
        question: 'What other work does One Vision Productions do?',
        answer: 'Beyond film production, One Vision Productions is known for their extraordinary work around children\'s mental health and community wellbeing. Led by Frank Coorey and Mark Robertson, their advocacy is quiet, consistent, and deeply impactful in the Northern Rivers community.'
      },
      {
        question: 'Where is One Vision Productions located?',
        answer: 'One Vision Productions is based in Byron Bay, New South Wales, Australia. You can learn more about their incredible work at onevisionproductions.com.au'
      }
    ]
  },
  
  'felipe-baldomir': {
    // Meta info
    category: 'MUSIC',
    publishDate: 'DEC 11, 2024',
    publishDateISO: '2024-12-11',
    modifiedDate: '2024-12-11',
    author: 'Free Flow Films',
    readTime: '4 min read',
    
    // SEO Meta
    metaTitle: 'Felipe Baldomir | Music Collaboration | Washed Away Documentary Soundtrack',
    metaDescription: 'Discover Felipe Baldomir, the Indie-Folk singer from Uruguay whose soulful music graces the Washed Away documentary soundtrack. A multi-instrumentalist musical architect.',
    keywords: 'Felipe Baldomir, Washed Away documentary, Indie-Folk music, Uruguay musician, documentary soundtrack, Australian music, Nature Speaks album, Only Light, soulful music',
    
    // Page content
    title: 'MUSIC COLLABORATION: FELIPE BALDOMIR',
    heroImage: 'https://customer-assets.emergentagent.com/job_bcb7959a-5c11-4a02-a3be-bbbaf5b483ed/artifacts/d9t0gxne_feleipe.jpg',
    heroImageAlt: 'Felipe Baldomir - Indie-Folk singer and songwriter from Uruguay',
    
    // Main content
    mainHeading: '',
    
    introParagraphs: [
      'Felipe Baldomir is an Indie-Folk singer and songwriter from Uruguay, South America.',
      'He is a multi-instrumentalist musical architect, who takes his audience on soulful sonic journeys.',
      '"While Felipe is from South America, his music has such a breezy Australian sound that no doubt comes from his ability to connect to country, to feel the sound of the ground that his two feet are standing upon."'
    ],
    
    // YouTube embed
    youtubeEmbed: {
      videoId: 'nu32nh7WR5k',
      title: 'Felipe Baldomir | Only Light (Live Acoustic Version)'
    },
    
    // Sections
    sections: [
      {
        title: '',
        paragraphs: [
          'Through his music he shares his reflections of people and places, of moments and memories – while listening you discover each song is a love letter to the person or place that inspired the creation of each melody.',
          'Since releasing his debut album, \'Nature Speaks\' in November 2017, Felipe has toured the South American region playing concerts and festivals in Argentina, Brazil, Uruguay and Paraguay. Even supporting Milky Chance on their last tour of Latin America.',
          'Not long after, his music journey was headed to the other side of the world. Starting in Indonesia, performing at iconic venues such as Old Mans, The Cashew Tree, and Deus Temple; and then soon Felipe made his way to Australia.',
          'Felipe is such a legend in our eyes.... Therefore we want to thank the incredibly gifted musician who recently shared his music for our feature documentary that\'s so inspiring and well received. Your distinctive contributions have genuinely lit up and inspired the Washed Away production team.'
        ]
      }
    ],
    
    // CTA
    ctaText: 'Stream "Washed Away" now at washedaway.au — featuring the beautiful music of Felipe Baldomir.',
    
    // Schema.org data
    personSchema: {
      name: 'Felipe Baldomir',
      description: 'Indie-Folk singer and songwriter from Uruguay, South America, featured on the Washed Away documentary soundtrack',
      jobTitle: 'Musician, Singer-Songwriter',
      knowsAbout: ['Indie-Folk music', 'Songwriting', 'Multi-instrumentalist', 'Live performances'],
    },
    
    // FAQ Schema for AEO
    faqSchema: [
      {
        question: 'Who is Felipe Baldomir?',
        answer: 'Felipe Baldomir is an Indie-Folk singer and songwriter from Uruguay, South America. He is a multi-instrumentalist musical architect who takes his audience on soulful sonic journeys. His music is featured in the Washed Away documentary.'
      },
      {
        question: 'What music did Felipe Baldomir contribute to Washed Away?',
        answer: 'Felipe Baldomir contributed his distinctive Indie-Folk sound to the Washed Away documentary soundtrack. His music has a breezy Australian sound that connects deeply with the land and spirit of the film.'
      },
      {
        question: 'What albums has Felipe Baldomir released?',
        answer: 'Felipe Baldomir released his debut album \'Nature Speaks\' in November 2017. He has since toured South America, supporting artists like Milky Chance, and performed at iconic venues in Indonesia and Australia.'
      },
      {
        question: 'Where can I hear Felipe Baldomir\'s music from Washed Away?',
        answer: 'You can experience Felipe Baldomir\'s music by streaming Washed Away at washedaway.au. The documentary features his soulful contributions throughout the film.'
      }
    ]
  },

  'rebecca-matkovic-helou': {
    // Meta info
    category: 'FILM · PEOPLE',
    publishDate: 'APR 22, 2026',
    publishDateISO: '2026-04-22',
    modifiedDate: '2026-04-22',
    author: 'Free Flow Films',
    readTime: '5 min read',
    
    // SEO Meta
    metaTitle: 'Rebecca Matkovic-Helou | Award-Winning Story Writer Behind Washed Away Documentary',
    metaDescription: 'Meet Rebecca Matkovic-Helou, the award-winning Australian film writer who crafted the narrative for Washed Away. With 19+ international awards and 15 years of experience, Rebecca shaped the documentary\'s powerful storytelling.',
    keywords: 'Rebecca Matkovic-Helou, Washed Away documentary, film writer, screenwriter, Australian storyteller, Quill and Raven, voiceover writer, documentary storytelling, Free Flow Films, Northern Rivers floods',
    
    // Page content
    title: 'MEET THE STORYTELLER BEHIND WASHED AWAY',
    heroImage: 'https://customer-assets.emergentagent.com/job_bcb7959a-5c11-4a02-a3be-bbbaf5b483ed/artifacts/2ccdzzss_PHOTO-2026-04-01-11-13-19.jpg',
    heroImageAlt: 'Rebecca Matkovic-Helou - Award-winning story writer behind Washed Away documentary',
    
    // Main content
    mainHeading: 'Rebecca Matkovic-Helou: The award-winning writer who shaped the soul of Washed Away',
    
    introParagraphs: [
      'At the heart of the storytelling of Washed Away is Rebecca Matkovic-Helou, an Australian award-winning film writer with extensive expertise in screenwriting, voiceover writing, scriptwriting, and story flow development. With over 15 years of experience in New York, Paris, and Melbourne, Rebecca has built a reputation for crafting narratives that connect deeply with audiences while honouring the authenticity of the stories she tells.',
      'She has received more than 19 international awards in film writing and storytelling. Her dedication to storytelling made her an integral part of bringing Washed Away to life.'
    ],
    
    // Multiple content sections
    sections: [
      {
        title: 'INSPIRED BY COMMUNITY STRENGTH',
        paragraphs: [
          'From the very beginning, Rebecca was inspired by the strength of the Northern Rivers community. Witnessing how they came together—providing raw footage, offering support, and contributing music—was a profoundly moving experience. Rebecca knew this film had to reflect their collective spirit and ensure their voices were heard.'
        ]
      },
      {
        title: '20 MONTHS OF DEDICATION',
        paragraphs: [
          'Rebecca worked tirelessly to shape the vision for Washed Away, collaborating closely with Terry and Stu from Free Flow Films. Over the next 20 months, she poured herself into every aspect of the narrative, meticulously reviewing countless hours of footage, crafting detailed notes, and revising the script through three major iterations.',
          'Her goal was to tell the community\'s story with honesty, care, and integrity, capturing their struggles and triumphs in a way that felt true to their experiences.'
        ]
      },
      {
        title: 'BEYOND STORYTELLING',
        paragraphs: [
          'Her collaboration extended beyond storytelling, guiding the production team on what needed to be filmed and ensuring the visuals supported the evolving narrative, as well as writing the voiceovers. With contributions from local musicians and the support of the community, Washed Away became more than a documentary—it became a tool for change, tied to a not-for-profit initiative aimed at driving funds back into the region.'
        ]
      },
      {
        title: 'A DREAM FOR THE COMMUNITY',
        paragraphs: [
          'Rebecca\'s dream for Washed Away is simple yet profound: to see smiles on the faces of the people who have struggled. Their community and strength have been at the heart of this journey, and her deepest hope is that this film brings them pride, hope, and the recognition they deserve.',
          'Grateful for the journey and inspired by the people she\'s met along the way, Rebecca looks forward to continuing her work with the Free Flow Films team, creating documentaries that not only inform and move audiences but also inspire real change.'
        ]
      },
      {
        title: 'COLLABORATE WITH REBECCA',
        paragraphs: [
          'To collaborate with Rebecca Matkovic-Helou or learn more about her work, visit www.quillandraven.com.',
          'With every project, Rebecca continues to craft stories that honour the human experience and leave a lasting legacy of hope and connection.'
        ],
        sectionLink: {
          url: 'https://www.quillandraven.com',
          text: 'Visit Quill and Raven'
        }
      }
    ],
    
    // CTA
    ctaText: 'Stream "Washed Away" now — experience the powerful storytelling crafted by Rebecca Matkovic-Helou.',
    
    // Schema.org data
    personSchema: {
      name: 'Rebecca Matkovic-Helou',
      alternateName: 'Award-Winning Film Writer',
      description: 'Australian award-winning film writer with 19+ international awards, who crafted the narrative for Washed Away documentary',
      jobTitle: 'Screenwriter, Story Producer',
      knowsAbout: ['Screenwriting', 'Documentary storytelling', 'Voiceover writing', 'Story development', 'Film writing'],
      sameAs: ['https://www.quillandraven.com']
    },
    
    // FAQ Schema for AEO
    faqSchema: [
      {
        question: 'Who wrote the story for Washed Away documentary?',
        answer: 'Rebecca Matkovic-Helou, an Australian award-winning film writer with over 15 years of experience and 19+ international awards, crafted the narrative for Washed Away. She worked closely with directors Terry Webster and Stuart McKay over 20 months to shape the documentary\'s storytelling.'
      },
      {
        question: 'What is Rebecca Matkovic-Helou known for?',
        answer: 'Rebecca Matkovic-Helou is known for her expertise in screenwriting, voiceover writing, scriptwriting, and story flow development. She has won more than 19 international awards in film writing and storytelling, with experience spanning New York, Paris, and Melbourne.'
      },
      {
        question: 'How can I work with Rebecca Matkovic-Helou?',
        answer: 'To collaborate with Rebecca Matkovic-Helou or learn more about her work in screenwriting and documentary storytelling, visit her website at www.quillandraven.com.'
      },
      {
        question: 'What was Rebecca\'s role in Washed Away?',
        answer: 'Rebecca served as the story writer and producer for Washed Away. Over 20 months, she reviewed countless hours of footage, crafted detailed notes, revised the script through three major iterations, guided the production team on filming, and wrote the voiceovers for the documentary.'
      }
    ]
  }
};

const StoryDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const story = storiesContent[slug];
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  // Set document title and meta tags
  useEffect(() => {
    if (story) {
      // Set title
      document.title = story.metaTitle;
      
      // Helper to set or create meta tag
      const setMetaTag = (name, content, isProperty = false) => {
        const attr = isProperty ? 'property' : 'name';
        let meta = document.querySelector(`meta[${attr}="${name}"]`);
        if (!meta) {
          meta = document.createElement('meta');
          meta.setAttribute(attr, name);
          document.head.appendChild(meta);
        }
        meta.setAttribute('content', content);
      };
      
      // Primary SEO meta tags
      setMetaTag('description', story.metaDescription);
      setMetaTag('keywords', story.keywords);
      setMetaTag('author', story.author);
      
      // Open Graph
      setMetaTag('og:type', 'article', true);
      setMetaTag('og:url', `https://washedaway.au/stories/${slug}`, true);
      setMetaTag('og:title', story.metaTitle, true);
      setMetaTag('og:description', story.metaDescription, true);
      setMetaTag('og:image', story.heroImage, true);
      setMetaTag('og:site_name', 'Washed Away Documentary', true);
      setMetaTag('article:published_time', story.publishDateISO, true);
      
      // Twitter
      setMetaTag('twitter:card', 'summary_large_image');
      setMetaTag('twitter:title', story.metaTitle);
      setMetaTag('twitter:description', story.metaDescription);
      setMetaTag('twitter:image', story.heroImage);
      
      // Canonical link
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        document.head.appendChild(canonical);
      }
      canonical.setAttribute('href', `https://washedaway.au/stories/${slug}`);
    }
    
    // Scroll to top
    window.scrollTo(0, 0);
    
    // Cleanup - restore original title on unmount
    return () => {
      document.title = 'Washed Away | Award-Winning Australian Documentary | Stream Now';
    };
  }, [slug, story]);

  // If story not found, redirect to stories page
  if (!story) {
    return (
      <div className="story-detail-page" data-testid="story-not-found">
        <div className="story-detail-container">
          <div className="story-not-found">
            <h1>Story Not Found</h1>
            <p>The story you're looking for doesn't exist yet.</p>
            <Link to="/stories" className="back-to-stories-btn">
              ← Back to Stories
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Generate JSON-LD structured data
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    'headline': story.title,
    'alternativeHeadline': story.mainHeading,
    'description': story.metaDescription,
    'image': story.heroImage,
    'datePublished': story.publishDateISO,
    'dateModified': story.modifiedDate,
    'author': {
      '@type': 'Organization',
      'name': story.author,
      'url': 'https://washedaway.au/'
    },
    'publisher': {
      '@type': 'Organization',
      'name': 'Washed Away Documentary',
      'logo': {
        '@type': 'ImageObject',
        'url': 'https://customer-assets.emergentagent.com/job_washed-away-cinema/artifacts/zlh4k7vd_LOGO.png'
      }
    },
    'mainEntityOfPage': {
      '@type': 'WebPage',
      '@id': `https://washedaway.au/stories/${slug}`
    },
    'keywords': story.keywords,
    'articleSection': 'Documentary Stories',
    'inLanguage': 'en-AU'
  };

  const personSchema = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    ...story.personSchema,
    'sameAs': [],
    'image': story.heroImage
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      {
        '@type': 'ListItem',
        'position': 1,
        'name': 'Home',
        'item': 'https://washedaway.au/'
      },
      {
        '@type': 'ListItem',
        'position': 2,
        'name': 'Stories',
        'item': 'https://washedaway.au/stories'
      },
      {
        '@type': 'ListItem',
        'position': 3,
        'name': story.title,
        'item': `https://washedaway.au/stories/${slug}`
      }
    ]
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': story.faqSchema.map(faq => ({
      '@type': 'Question',
      'name': faq.question,
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': faq.answer
      }
    }))
  };

  return (
    <>
      {/* JSON-LD Schema Scripts for SEO/AEO */}
      <script 
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(JSON.stringify(articleSchema), { ALLOWED_TAGS: [] }) }}
      />
      <script 
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(JSON.stringify(personSchema), { ALLOWED_TAGS: [] }) }}
      />
      <script 
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(JSON.stringify(breadcrumbSchema), { ALLOWED_TAGS: [] }) }}
      />
      <script 
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(JSON.stringify(faqSchema), { ALLOWED_TAGS: [] }) }}
      />

      <article className={`story-detail-page ${story.heroStyle === 'banner' ? 'hero-banner-style' : ''}`} data-testid="story-detail-page" itemScope itemType="https://schema.org/Article">
        {/* Background */}
        <div className="story-detail-background"></div>
        
        {/* Top Navigation Bar */}
        <nav className="story-top-nav" data-testid="story-top-nav">
          <Link to="/" className="story-nav-link">Home</Link>
          <Link to="/stories" className="story-nav-link">Stories</Link>
        </nav>
        
        {/* Hero Banner Style */}
        {story.heroStyle === 'banner' ? (
          <>
            <div className="story-hero-banner">
              <img 
                src={story.heroImage} 
                alt={story.heroImageAlt}
                itemProp="image"
                loading="eager"
                className="hero-banner-image"
              />
              <div className="hero-banner-overlay"></div>
              <div className="hero-banner-content">
                <span className="hero-banner-category">{story.category}</span>
                <h1 className="hero-banner-title" itemProp="headline">{story.title}</h1>
                <p className="hero-banner-subtitle">One Vision Productions</p>
              </div>
            </div>
            
            <div className="story-detail-container">
              {/* Share Buttons */}
              <ShareButtons 
                title={story.title}
                url={`https://washedaway.au/stories/${slug}`}
                description={story.metaDescription}
              />
              
              {/* Article Content */}
              <div className="story-detail-content" itemProp="articleBody">
                {/* Main Heading */}
                {story.mainHeading && (
                  <h2 className="story-content-heading story-banner-heading">{story.mainHeading}</h2>
                )}
                
                {/* Intro Paragraphs */}
                {story.introParagraphs.map((para, idx) => (
                  <p key={`intro-${slug}-${idx}`} className={`story-paragraph ${para.startsWith('"') ? 'story-quote' : ''}`}>{para}</p>
                ))}

                {/* Vimeo Embed (if available) */}
                {story.vimeoEmbed && (
                  <div className="story-vimeo-embed">
                    <iframe
                      src={`https://player.vimeo.com/video/${story.vimeoEmbed.videoId}?badge=0&autopause=0&player_id=0&app_id=58479`}
                      title={story.vimeoEmbed.title}
                      frameBorder="0"
                      allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                      allowFullScreen
                    ></iframe>
                  </div>
                )}

                {/* Multiple Content Sections */}
                {story.sections && story.sections.map((section, idx) => (
                  <section key={`section-${slug}-${idx}`} className="story-content-section">
                    {section.title && <h3 className="story-section-title">{section.title}</h3>}
                    {section.paragraphs && section.paragraphs.map((para, pIdx) => (
                      <p key={`para-${slug}-${idx}-${pIdx}`} className="story-paragraph">{para}</p>
                    ))}
                  </section>
                ))}

                {/* External Link Button */}
                {story.externalLink && (
                  <div className="story-external-link-container">
                    <a 
                      href={story.externalLink.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="story-external-link-button"
                    >
                      {story.externalLink.text} →
                    </a>
                  </div>
                )}

                {/* CTA */}
                <p className="story-cta-text">{story.ctaText}</p>

                {/* Stream Button */}
                <div className="story-cta-button-container">
                  <button 
                    onClick={() => setIsModalOpen(true)} 
                    className="stream-cta-button" 
                    data-testid="story-stream-cta"
                  >
                    Stream Now — $12.69 AUD
                  </button>
                </div>
              </div>

              {/* FAQ Section */}
              <section className="story-faq-section" itemScope itemType="https://schema.org/FAQPage">
                <h2 className="story-faq-title">Frequently Asked Questions</h2>
                <div className="story-faq-list">
                  {story.faqSchema.map((faq, idx) => (
                    <div 
                      key={`faq-${slug}-${idx}`} 
                      className="story-faq-item" 
                      itemScope 
                      itemProp="mainEntity" 
                      itemType="https://schema.org/Question"
                    >
                      <h3 className="story-faq-question" itemProp="name">{faq.question}</h3>
                      <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                        <p className="story-faq-answer" itemProp="text">{faq.answer}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* Back to Stories */}
              <nav className="story-detail-footer">
                <Link to="/stories" className="back-to-stories-link">
                  ← Back to All Stories
                </Link>
              </nav>
            </div>
          </>
        ) : (
          /* Standard Story Layout */
          <div className="story-detail-container">
            {/* Article Header */}
            <header className="story-detail-header">
              <div className="story-meta-top">
                <span className="story-category">{story.category}</span>
                <span className="story-meta-separator">·</span>
                <time dateTime={story.publishDateISO} itemProp="datePublished">{story.publishDate}</time>
              </div>
              <h1 className="story-detail-title" itemProp="headline">{story.title}</h1>
            </header>

            {/* Hero Image */}
            <figure className="story-hero-image">
              <img 
                src={story.heroImage} 
                alt={story.heroImageAlt}
                itemProp="image"
                loading="eager"
                width="1200"
                height="675"
              />
            </figure>

            {/* Share Buttons */}
            <ShareButtons 
              title={story.title}
              url={`https://washedaway.au/stories/${slug}`}
              description={story.metaDescription}
            />

            {/* Article Content */}
            <div className="story-detail-content" itemProp="articleBody">
              {/* Main Heading (optional - some stories flow without it) */}
              {story.mainHeading && (
                <h2 className="story-content-heading">{story.mainHeading}</h2>
              )}
              
              {/* Intro Paragraphs */}
              {story.introParagraphs.map((para, idx) => (
                <p key={`std-intro-${slug}-${idx}`} className={`story-paragraph ${para.startsWith('"') ? 'story-quote' : ''}`}>{para}</p>
              ))}

              {/* YouTube Embed (if available) */}
              {story.youtubeEmbed && (
                <div className="story-youtube-embed">
                  <iframe
                    src={`https://www.youtube.com/embed/${story.youtubeEmbed.videoId}`}
                    title={story.youtubeEmbed.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              )}

              {/* Vimeo Embed (if available) */}
              {story.vimeoEmbed && (
                <div className="story-vimeo-embed">
                  <iframe
                    src={`https://player.vimeo.com/video/${story.vimeoEmbed.videoId}?badge=0&autopause=0&player_id=0&app_id=58479`}
                    title={story.vimeoEmbed.title}
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                    allowFullScreen
                  ></iframe>
                </div>
              )}

            {/* Multiple Content Sections (new format) */}
            {story.sections && story.sections.map((section, idx) => (
              <section key={`std-section-${slug}-${idx}`} className="story-content-section">
                <h3 className="story-section-title">{section.title}</h3>
                {section.paragraphs && section.paragraphs.map((para, pIdx) => (
                  <p key={`std-para-${slug}-${idx}-${pIdx}`} className="story-paragraph">{para}</p>
                ))}
                {section.bulletPoints && (
                  <ul className="story-unique-list">
                    {section.bulletPoints.map((point, bIdx) => (
                      <li key={`std-bullet-${slug}-${idx}-${bIdx}`}>{point}</li>
                    ))}
                  </ul>
                )}
                {section.sectionLink && (
                  <a 
                    href={section.sectionLink.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="story-section-link"
                  >
                    {section.sectionLink.text} →
                  </a>
                )}
              </section>
            ))}

            {/* Legacy Unique Section (old format - for backwards compatibility) */}
            {story.uniqueSectionTitle && (
              <section className="story-unique-section">
                <h3 className="story-section-title">{story.uniqueSectionTitle}</h3>
                <ul className="story-unique-list">
                  {story.uniquePoints.map((point, idx) => (
                    <li key={`legacy-point-${slug}-${idx}`}>{point}</li>
                  ))}
                </ul>
              </section>
            )}

            {/* Legacy Body Paragraphs (old format) */}
            {story.bodyParagraphs && story.bodyParagraphs.map((para, idx) => (
              <p key={`legacy-body-${slug}-${idx}`} className="story-paragraph">{para}</p>
            ))}

            {/* External Link Button (if available) */}
            {story.externalLink && (
              <div className="story-external-link-container">
                <a 
                  href={story.externalLink.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="story-external-link-button"
                >
                  {story.externalLink.text} →
                </a>
              </div>
            )}

            {/* CTA */}
            <p className="story-cta-text">{story.ctaText}</p>

            {/* Stream Button - matches home page exactly */}
            <div className="story-cta-button-container">
              <button 
                onClick={() => setIsModalOpen(true)} 
                className="stream-cta-button" 
                data-testid="story-stream-cta"
              >
                Stream Now — $12.69 AUD
              </button>
            </div>
          </div>

          {/* FAQ Section for AEO */}
          <section className="story-faq-section" itemScope itemType="https://schema.org/FAQPage">
            <h2 className="story-faq-title">Frequently Asked Questions</h2>
            <div className="story-faq-list">
              {story.faqSchema.map((faq, idx) => (
                <div 
                  key={`std-faq-${slug}-${idx}`} 
                  className="story-faq-item" 
                  itemScope 
                  itemProp="mainEntity" 
                  itemType="https://schema.org/Question"
                >
                  <h3 className="story-faq-question" itemProp="name">{faq.question}</h3>
                  <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                    <p className="story-faq-answer" itemProp="text">{faq.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Back to Stories */}
          <nav className="story-detail-footer">
            <Link to="/stories" className="back-to-stories-link">
              ← Back to All Stories
            </Link>
          </nav>
        </div>
        )}
        
        {/* Sticky Mobile CTA */}
        {!isPreviewOpen && (
          <div className="story-sticky-mobile-cta">
            <button 
              onClick={() => setIsModalOpen(true)} 
              className="story-sticky-cta-button" 
              data-testid="story-sticky-cta"
            >
              Stream Now · $12.69
            </button>
            <button
              onClick={() => setIsPreviewOpen(true)}
              className="story-sticky-preview-link"
              data-testid="story-sticky-preview"
            >
              Watch 8 mins free →
            </button>
          </div>
        )}
        
        {/* 8 Minute Preview Modal */}
        {isPreviewOpen && (
          <div 
            className="preview-modal-overlay"
            onClick={() => setIsPreviewOpen(false)}
            data-testid="story-preview-modal"
          >
            <div className="preview-modal-content" onClick={(e) => e.stopPropagation()}>
              <button 
                className="preview-modal-close"
                onClick={() => setIsPreviewOpen(false)}
                aria-label="Close preview"
              >
                ✕
              </button>
              <div className="preview-modal-video">
                <iframe
                  src="https://player.vimeo.com/video/1185618783?autoplay=1&title=0&byline=0&portrait=0"
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  title="Watch 8 Minutes Free - Washed Away Preview"
                ></iframe>
              </div>
              <div className="preview-modal-cta">
                <p className="preview-modal-text">Like what you see?</p>
                <button
                  onClick={() => {
                    setIsPreviewOpen(false);
                    setIsModalOpen(true);
                  }}
                  className="preview-stream-button"
                  data-testid="story-preview-stream-button"
                >
                  Stream Full Documentary · $12.69
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Payment Modal */}
        <PaymentModal 
          isOpen={isModalOpen} 
          onClose={() => setIsModalOpen(false)} 
        />
      </article>
    </>
  );
};

export default StoryDetailPage;
export { storiesContent };
